import React, { useState } from 'react';
import styles from './PatientInfoStep.module.css';
import AxisLogo from './SVGs/AxisLogo';

interface PatientInfoStepProps {
  onBack: () => void;
  onProceed: (formData: PatientInfoFormData) => void;
  initialData?: PatientInfoFormData | null;
  patientData?: any; // Data from PatientStatusStep
  isSpravato?: boolean; // Flag to identify Spravato appointments
}

export interface PatientInfoFormData {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: {
    day: string;
    month: string;
    year: string;
  };
  sex: string;
  phoneType: string;
  phoneNumber: string;
  allowTextMessages: boolean;
  address: string;
  zipCode: string;
  insurance: string;
  insuranceId: string;
  reasonForVisit: string;
  howDidYouHear: string;
}

const defaultForm: PatientInfoFormData = {
  firstName: '',
  lastName: '',
  email: '',
  dateOfBirth: {
    day: '',
    month: '',
    year: ''
  },
  sex: '',
  phoneType: 'Mobile',
  phoneNumber: '',
  allowTextMessages: true,
  address: '',
  zipCode: '',
  insurance: '',
  insuranceId: '',
  reasonForVisit: '',
  howDidYouHear: ''
};

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString());
const years = Array.from({ length: 100 }, (_, i) => (2024 - i).toString());

const insuranceOptions = [
  'Aetna',
  'Anthem-Blue Cross Blue Shield',
  'BCBS-FEDERAL',
  'Carelon Behavioral Health',
  'CIGNA',
  'Cofinity',
  'Colorado Medicaid (with the exception of Denver Health Medicaid)',
  'Optum',
  'Self Pay',
  'UMR',
  'United Healthcare Medicare',
  'United Healthcare (with the exception of Rocky Mountain Health Plan)',
  'Denver Health Medical Plan'
];

const PatientInfoStep: React.FC<PatientInfoStepProps> = ({ 
  onBack, 
  onProceed, 
  initialData,
  patientData,
  isSpravato = false
}) => {
  // Component initialization

  const [form, setForm] = useState<PatientInfoFormData>(defaultForm);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Determine if patient is existing (has athenaPatientId)
  const isExistingPatient = (): boolean => {
    return !!(patientData?.existingPatient === true && patientData?.athenaPatientId);
  };

  // Determine if a field should be read-only (only for existing patients with confirmed data)
  const isFieldReadOnly = (): boolean => {
    // Allow editing on PatientInfoStep; fields are pre-filled but editable
    return false;
  };

  // Update form when initialData or patientData changes
  React.useEffect(() => {
    if (initialData) {
      setForm(initialData);
    } else if (patientData) {
      // Convert numeric month to month name
      const monthNumberToName: Record<string, string> = {
        '01': 'January', '02': 'February', '03': 'March', '04': 'April',
        '05': 'May', '06': 'June', '07': 'July', '08': 'August',
        '09': 'September', '10': 'October', '11': 'November', '12': 'December'
      };
      
      const dateOfBirth = patientData.dateOfBirth ? {
        day: patientData.dateOfBirth.split('-')[2] || '',
        month: monthNumberToName[patientData.dateOfBirth.split('-')[1]] || patientData.dateOfBirth.split('-')[1] || '',
        year: patientData.dateOfBirth.split('-')[0] || ''
      } : { day: '', month: '', year: '' };
      
      const preFilledForm = {
        firstName: patientData.firstName || '',
        lastName: patientData.lastName || '',
        email: patientData.email || '',
        dateOfBirth,
        sex: patientData.sex || '',
        phoneType: 'Mobile',
        phoneNumber: patientData.phone || patientData.phoneNumber || '',
        allowTextMessages: true,
        address: patientData.address || '',
        zipCode: patientData.zipCode || '',
        insurance: '',
        insuranceId: '',
        reasonForVisit: '',
        howDidYouHear: ''
      };
      
      setForm(preFilledForm);
    } else {
      setForm(defaultForm);
    }
  }, [initialData, patientData]);

  // Different required fields based on patient status
  const getRequiredFields = (): (keyof PatientInfoFormData)[] => {
    if (isExistingPatient()) {
      // For existing patients, require insurance fields for Spravato appointments
      if (isSpravato) {
        return ['insurance', 'insuranceId'];
      }
      // For existing patients, only reasonForVisit and howDidYouHear are optional
      return [];
    } else {
      // For new patients, require all personal info (phoneNumber is validated separately)
      return ['firstName', 'lastName', 'dateOfBirth', 'sex', 'phoneNumber', 'address', 'zipCode', 'insurance', 'insuranceId'];
    }
  };

  const requiredFields = getRequiredFields();

  const validate = () => {
    const errors: Record<string, string> = {};
    
    // Validate required fields for new patients
    if (!isExistingPatient()) {
      requiredFields.forEach(field => {
        if (field === 'dateOfBirth') {
          const dob = form.dateOfBirth;
          if (!dob || !dob.day || !dob.month || !dob.year) {
            errors.dateOfBirth = 'Required';
          }
        } else {
          const value = form[field as keyof PatientInfoFormData];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            errors[field] = 'Required';
          }
        }
      });

      // Phone number is required for new patients
      if (!form.phoneNumber || form.phoneNumber.trim() === '') {
        errors.phoneNumber = 'Phone number is required';
      } else {
        // Validate phone format (should be 10 digits minimum)
        const phoneDigits = form.phoneNumber.replace(/\D/g, '');
        if (phoneDigits.length < 10) {
          errors.phoneNumber = 'Please enter a valid phone number';
        }
      }

      // Email is optional, but validate format if provided
      if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
        errors.email = 'Invalid email';
      }

      if (form.zipCode && !/^\d{5,}$/.test(form.zipCode)) {
        errors.zipCode = 'Invalid zip code';
      }
    } else {
      // For existing patients, validate insurance fields if it's a Spravato appointment
      if (isSpravato) {
        requiredFields.forEach(field => {
          const value = form[field as keyof PatientInfoFormData];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            errors[field] = 'Required';
          }
        });
      }
    }
    
    return errors;
  };

  const errors = validate();
  const isValid = Object.keys(errors).length === 0;
  
  // Form validation state
  

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setForm(f => ({ ...f, [name]: checked }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const handleDateChange = (field: 'day' | 'month' | 'year', value: string) => {
    setForm(f => ({
      ...f,
      dateOfBirth: { ...f.dateOfBirth, [field]: value }
    }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setTouched(t => ({ ...t, [e.target.name]: true }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(Object.fromEntries(Object.keys(form).map(k => [k, true])));
    if (isValid) {
      if (isExistingPatient()) {
        // For existing patients, pass appointment-specific information
        const formDataForAPI = {
          reasonForVisit: form.reasonForVisit,
          howDidYouHear: form.howDidYouHear,
          // Include insurance information for Spravato appointments
          ...(isSpravato && {
            insurance: form.insurance,
            insuranceId: form.insuranceId
          }),
          // Include patient data from the API response
          athenaPatientId: patientData?.athenaPatientId,
          existingPatient: true
        };
        onProceed(formDataForAPI as any);
      } else {
        // For new patients, convert dateOfBirth to MM/DD/YYYY string for API
        const monthNameToNumber: Record<string, string> = {
          January: '01', February: '02', March: '03', April: '04', May: '05', June: '06',
          July: '07', August: '08', September: '09', October: '10', November: '11', December: '12'
        };
        const dd = `${form.dateOfBirth.day}`.padStart(2, '0');
        const mm = (monthNameToNumber[form.dateOfBirth.month] || `${form.dateOfBirth.month}`).padStart(2, '0');
        const yyyy = `${form.dateOfBirth.year}`;
        const formDataForAPI = {
          ...form,
          dateOfBirth: `${mm}/${dd}/${yyyy}`
        };
        onProceed(formDataForAPI as any);
      }
    }
  };

  return (
    <div className={styles.patientInformationFilled}>
      {/* Header/Navigation */}
      <div className={styles.navBar}>
        <AxisLogo variant="white" size="extra-large" className={styles.navLogo} onClick={() => window.open('https://www.axismh.com/', '_blank')} style={{ cursor: 'pointer' }} />
        <span className={styles.navPhone}>(720) 400-7025</span>
        <div className={styles.navLinks}>
          <button className={styles.navLink} onClick={() => window.location.reload()}>HOME</button>
        </div>
        {/* <div className={styles.navMenuIcon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12h18M3 6h18M3 18h18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div> */}
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <h1 className={styles.title}>
          {isExistingPatient() ? 'Appointment Details' : 'Patient Information'}
        </h1>
        <p className={styles.subtitle}>
          {isExistingPatient() 
            ? 'Please provide additional information for your appointment.'
            : 'Please provide your personal and insurance information to complete your booking.'
          }
        </p>

        <div className={styles.card}>
          {isExistingPatient() ? (
            <>
              <div className={styles.sectionTitle}>Appointment Details</div>
              <div className={styles.sectionSubtitle}>
                {isSpravato 
                  ? 'Please provide additional information for your appointment. Insurance information is required for Spravato appointments.'
                  : 'Please provide additional information for your appointment.'
                }
              </div>
              <div style={{ color: '#10b981', fontSize: '14px', marginBottom: '16px' }}>
                ✓ We have your information on file. Please provide the details below.
              </div>
            </>
          ) : (
            <>
              <div className={styles.sectionTitle}>Personal Information</div>
              <div className={styles.sectionSubtitle}>All fields marked with * are required. Phone number is required.</div>
              {patientData && patientData.existingPatient !== true && (
                <div style={{ color: '#3b82f6', fontSize: '14px', marginBottom: '16px' }}>
                  ℹ Pre-filled with information from previous step - you can edit as needed
                </div>
              )}
            </>
          )}

          <form onSubmit={handleSubmit}>
            <div className={styles.formGrid}>
              {/* Personal Information Fields - Only show for new patients */}
              {!isExistingPatient() && (
                <>
                  {/* First Name */}
                  <div className={styles.formField}>
                <label className={styles.formLabel}>
                  First Name <span className={styles.required}>*</span>
                </label>
                <input 
                  name="firstName" 
                  value={form.firstName} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  className={styles.formInput}
                  readOnly={isFieldReadOnly()}
                />
                {touched.firstName && errors.firstName && <div className={styles.formError}>{errors.firstName}</div>}
              </div>

              {/* Last Name */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Last Name <span className={styles.required}>*</span>
                </label>
                <input 
                  name="lastName" 
                  value={form.lastName} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  className={styles.formInput}
                  readOnly={isFieldReadOnly()}
                />
                {touched.lastName && errors.lastName && <div className={styles.formError}>{errors.lastName}</div>}
              </div>

              {/* Email Address */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Email Address <span className={styles.optional}>(optional)</span>
                </label>
                <input 
                  name="email" 
                  value={form.email} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  className={styles.formInput}
                  type="email" 
                  readOnly={isFieldReadOnly()}
                />
                {touched.email && errors.email && <div className={styles.formError}>{errors.email}</div>}
              </div>

              {/* Date of Birth */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Date of Birth <span className={styles.required}>*</span>
                </label>
                <div className={styles.dobFields}>
                  <div className={styles.dobField}>
                    <select 
                      value={form.dateOfBirth.day} 
                      onChange={(e) => handleDateChange('day', e.target.value)}
                      onBlur={() => setTouched(t => ({ ...t, dateOfBirth: true }))}
                      className={styles.formSelect}
                      disabled={isFieldReadOnly()}
                    >
                      <option value="">Day</option>
                      {days.map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.dobField}>
                    <select 
                      value={form.dateOfBirth.month} 
                      onChange={(e) => handleDateChange('month', e.target.value)}
                      onBlur={() => setTouched(t => ({ ...t, dateOfBirth: true }))}
                      className={styles.formSelect}
                      disabled={isFieldReadOnly()}
                    >
                      <option value="">Month</option>
                      {months.map(month => (
                        <option key={month} value={month}>{month}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.dobField}>
                    <select 
                      value={form.dateOfBirth.year} 
                      onChange={(e) => handleDateChange('year', e.target.value)}
                      onBlur={() => setTouched(t => ({ ...t, dateOfBirth: true }))}
                      className={styles.formSelect}
                      disabled={isFieldReadOnly()}
                    >
                      <option value="">Year</option>
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {touched.dateOfBirth && errors.dateOfBirth && <div className={styles.formError}>{errors.dateOfBirth}</div>}
              </div>

              {/* Sex */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Sex <span className={styles.required}>*</span>
                </label>
                <select 
                  name="sex" 
                  value={form.sex} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  className={styles.formSelect}
                  disabled={isFieldReadOnly()}
                >
                  <option value="">Select Sex</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {touched.sex && errors.sex && <div className={styles.formError}>{errors.sex}</div>}
              </div>

              {/* Phone Number */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Phone Number <span className={styles.required}>*</span>
                </label>
                <div className={styles.phoneFields}>
                  <div className={styles.phoneType}>
                    <select 
                      name="phoneType" 
                      value={form.phoneType} 
                      onChange={handleChange} 
                      className={styles.formSelect}
                    >
                      <option value="Mobile">Mobile</option>
                      <option value="Home">Home</option>
                    </select>
                  </div>
                  <div className={styles.phoneNumber}>
                    <input 
                      name="phoneNumber" 
                      value={form.phoneNumber} 
                      onChange={handleChange} 
                      onBlur={handleBlur} 
                      className={styles.formInput}
                      type="tel" 
                      placeholder="4155551234"
                      readOnly={isFieldReadOnly()}
                    />
                  </div>
                </div>
                <div className={styles.phoneFormatSuggestion}>
                  Phone numbers must follow the North American Numbering Plan. Should be of format: NPANXXXXXX.
                </div>
                {touched.phoneNumber && errors.phoneNumber && <div className={styles.formError}>{errors.phoneNumber}</div>}
              </div>


              {/* Street Address */}
              <div className={styles.formFieldFull}>
                <label className={styles.formLabel}>
                  Street Address <span className={styles.required}>*</span>
                </label>
                <input 
                  name="address" 
                  value={form.address} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  className={styles.formInput}
                />
                {touched.address && errors.address && <div className={styles.formError}>{errors.address}</div>}
              </div>

              {/* Zip Code */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Zip Code <span className={styles.required}>*</span>
                </label>
                <input 
                  name="zipCode" 
                  value={form.zipCode} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  className={styles.formInput}
                />
                {touched.zipCode && errors.zipCode && <div className={styles.formError}>{errors.zipCode}</div>}
              </div>

              {/* Insurance */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Insurance <span className={styles.required}>*</span>
                </label>
                <select 
                  name="insurance" 
                  value={form.insurance} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  className={styles.formSelect}
                >
                  <option value="">Select Insurance</option>
                  {insuranceOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {touched.insurance && errors.insurance && <div className={styles.formError}>{errors.insurance}</div>}
              </div>

              {/* Insurance Member ID */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Insurance Member ID <span className={styles.required}>*</span>
                </label>
                <input 
                  name="insuranceId" 
                  value={form.insuranceId} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  className={styles.formInput}
                />
                {touched.insuranceId && errors.insuranceId && <div className={styles.formError}>{errors.insuranceId}</div>}
              </div>
                </>
              )}

              {/* Reason for Visit - Show for all patients */}
              <div className={styles.formFieldFull}>
                <label className={styles.formLabel}>Reason for Visit</label>
                <textarea 
                  name="reasonForVisit" 
                  value={form.reasonForVisit} 
                  onChange={handleChange} 
                  className={styles.formTextarea}
                  placeholder="Please describe the reason for your visit..."
                  rows={3}
                />
              </div>

              {/* Insurance fields for existing patients with Spravato appointments */}
              {isExistingPatient() && isSpravato && (
                <>
                  {/* Insurance */}
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>
                      Insurance <span className={styles.required}>*</span>
                    </label>
                    <select 
                      name="insurance" 
                      value={form.insurance} 
                      onChange={handleChange} 
                      onBlur={handleBlur} 
                      className={styles.formSelect}
                    >
                      <option value="">Select Insurance</option>
                      {insuranceOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    {touched.insurance && errors.insurance && <div className={styles.formError}>{errors.insurance}</div>}
                  </div>

                  {/* Insurance Member ID */}
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>
                      Insurance Member ID <span className={styles.required}>*</span>
                    </label>
                    <input 
                      name="insuranceId" 
                      value={form.insuranceId} 
                      onChange={handleChange} 
                      onBlur={handleBlur} 
                      className={styles.formInput}
                    />
                    {touched.insuranceId && errors.insuranceId && <div className={styles.formError}>{errors.insuranceId}</div>}
                  </div>
                </>
              )}

              {/* How did you hear about us */}
              <div className={styles.formFieldFull}>
                <label className={styles.formLabel}>How did you hear about us?</label>
                <textarea 
                  name="howDidYouHear" 
                  value={form.howDidYouHear} 
                  onChange={handleChange} 
                  className={styles.formTextarea}
                  placeholder="Please type here.."
                  rows={3}
                />
              </div>
            </div>

            <button
              className={styles.proceedButton}
              type="submit"
              disabled={!isValid}
            >
              Proceed to Confirmation
            </button>
          </form>
        </div>

        <button className={styles.backButton} onClick={onBack}>
          ← Back
        </button>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
        <div className={styles.footerInfo}>
          <div className={styles.footerInfoItem}>
            <span>📞</span>
            <span>Phone (appointments): 720-400-7025</span>
          </div>
          <div className={styles.footerInfoItem}>
            <span>📍</span>
          </div>
        </div>
        <div className={styles.rating}>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
        </div>
        <div className={styles.ratingText}>Average Rating: 4.9/5</div>
      </div>

      {/* Accessibility Icon */}
      <div className={styles.accessibilityIcon}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="4" r="2" fill="white"/>
          <path d="M12 6v8M8 14h8M12 18v4M8 22h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  );
};

export default PatientInfoStep; 